import { Message, Modal } from '@bitcine/cinesend-theme/dist'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import useStore from '../../hooks/use_store'
import { Fragment } from 'react'

const SchemaMappingModal = ({ onClose, cycle, mappingData, invalidateKeys }) => {
  const queryClient = useQueryClient()
  const { updateModel } = useStore()
  const { data: languages } = useQuery(['/api/languages'])

  const openCycleWithApproval = () => {
    updateModel.mutate(
      {
        endpoint: 'cycles',
        id: `${cycle.id}/open?approveSchemaChange=true`,
      },
      {
        onSuccess: () => {
          onClose()
          queryClient.invalidateQueries(invalidateKeys)
        },
        onError: (err) => console.log(err),
      }
    )
  }

  const hasDroppedFields =
    mappingData['fieldMappings']['dropped'].length !== 0 || mappingData['collectionMappings']['dropped'].length !== 0

  const gridAlternateBg = (index) => `${index % 2 == 0 ? 'bg-slate-100' : ''} py-2 px-1`

  return (
    <Modal
      header={`Approve Schema Change`}
      onClose={onClose}
      confirmButton={{
        text: 'Approve and Open',
        onClick: () => openCycleWithApproval(),
      }}
    >
      <p className='pb-4'>
        <span className='font-bold'>Cycle:</span> {cycle.description}
      </p>
      <p className='pb-4'>
        Schema changing from "<span className='font-bold'>{mappingData.lastSchema.name}</span>" to "
        <span className='font-bold'>{mappingData.newSchema.name}</span>"
      </p>
      {hasDroppedFields ? (
        <Message warning={true} small={true}>
          Some fields or list items were not able to be mapped from the previous schema, check the lists below to ensure
          their removal was intentional.
        </Message>
      ) : (
        <Message small={true} icon='check'>
          All previous fields and list items have a match in the new schema!
        </Message>
      )}
      <div className='grid grid-cols-[50%_50%] gap-x-4 gap-y-2 mt-4'>
        <div>
          <div className='grid grid-cols-2'>
            <p className='font-bold col-span-2'>Fields</p>
            <span>Matched:</span>
            <span>{Object.keys(mappingData.fieldMappings.matched).length}</span>
            <span>New:</span>
            <span>{mappingData.fieldMappings.new.length}</span>
            <span>Removed:</span>
            <span>{mappingData.fieldMappings.dropped.length}</span>
          </div>
          <div className='grid grid-cols-3'>
            {mappingData.fieldMappings.dropped.length > 0 && (
              <>
                <p className='font-bold mt-4 col-span-3'>Removed Fields</p>
                <span className='font-bold mt-4'>Name</span>
                <span className='font-bold mt-4'>Type</span>
                <span className='font-bold mt-4'>Asset Types</span>
                {mappingData.fieldMappings.dropped.map((field, index) => (
                  <Fragment key={field.id}>
                    <span className={gridAlternateBg(index)}>{field.name}</span>
                    <span className={gridAlternateBg(index)}>{field.field_type}</span>
                    <span className={gridAlternateBg(index)}>{field.asset_types.join(', ')}</span>
                  </Fragment>
                ))}
              </>
            )}
          </div>
        </div>
        <div className='flex flex-col'>
          <div className='grid grid-cols-2'>
            <p className='font-bold col-span-2'>List Values</p>
            <span>Matched:</span>
            <span>{Object.keys(mappingData.collectionMappings.matched).length}</span>
            <span>New:</span>
            <span>{mappingData.collectionMappings.new.length}</span>
            <span>Removed:</span>
            <span>{mappingData.collectionMappings.dropped.length}</span>
          </div>
          <div className='grid grid-cols-3'>
            {mappingData.collectionMappings.dropped.length > 0 && (
              <>
                <p className='font-bold mt-4 col-span-3'>Removed List Values</p>
                <span className='font-bold mt-4'>List</span>
                <span className='font-bold mt-4'>Value</span>
                <span className='font-bold mt-4'>Lang Code</span>
                {mappingData.collectionMappings.dropped.map((value, index) => (
                  <Fragment key={value.id}>
                    <span className={gridAlternateBg(index)}>{value.collection?.name ?? 'unknown collection'}</span>
                    <span className={gridAlternateBg(index)}>{value.label}</span>
                    <span className={gridAlternateBg(index)}>
                      {languages?.filter((lang) => lang.id === value.language_id)?.[0]?.ife_code ?? 'default'}
                    </span>
                  </Fragment>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SchemaMappingModal
